import React, { useCallback, useRef, useState, type SetStateAction } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Upload, X, Loader2, GripVertical } from 'lucide-react';
import { uploadToStorage } from '../services/supabase';

export interface UploadedImage {
  id: string;
  preview: string;
  url: string | null;
  path: string | null;
  uploading: boolean;
  width?: number;
  height?: number;
}

interface ImageUploaderProps {
  images: UploadedImage[];
  onChange: (value: SetStateAction<UploadedImage[]>) => void;
  userId: string;
  maxImages?: number;
  onUploadError?: (message: string) => void;
}

function SortableImage({
  image,
  onRemove,
}: {
  image: UploadedImage;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group rounded-lg overflow-hidden border-2 border-slate-700 bg-slate-800"
    >
      <img
        src={image.preview}
        alt=""
        className="w-full max-h-[50vh] object-contain mx-auto"
      />

      {image.uploading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      )}

      <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="p-1 bg-black/60 rounded text-white cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.85;

function preprocessImage(file: File): Promise<{ file: File; width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve({ file, width, height });
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve({ file, width, height });
            return;
          }
          const name = file.name.replace(/\.\w+$/, '.jpg');
          resolve({ file: new File([blob], name, { type: 'image/jpeg' }), width, height });
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => resolve({ file, width: 0, height: 0 });
    img.src = URL.createObjectURL(file);
  });
}

export default function ImageUploader({
  images,
  onChange,
  userId,
  maxImages = 1,
  onUploadError,
}: ImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleFiles = useCallback(
    async (fileList: FileList) => {
      const files = Array.from(fileList).filter((f) =>
        f.type.startsWith('image/')
      );
      const remaining = maxImages - images.length;
      const toUpload = files.slice(0, remaining);
      if (toUpload.length === 0) return;

      const newImages: UploadedImage[] = toUpload.map((file) => ({
        id: crypto.randomUUID(),
        preview: URL.createObjectURL(file),
        url: null,
        path: null,
        uploading: true,
      }));

      onChange([...images, ...newImages]);

      for (let i = 0; i < toUpload.length; i++) {
        try {
          const { file: processed, width, height } = await preprocessImage(toUpload[i]);
          const result = await uploadToStorage(processed, 'images', userId);
          onChange((prev) =>
            prev.map((img) =>
              img.id === newImages[i].id
                ? { ...img, url: result.url, path: result.path, uploading: false, width, height }
                : img
            )
          );
        } catch (err) {
          let message = 'Upload failed. Please try again.';
          if (err instanceof Error && err.message) {
            message = err.message;
          } else if (err && typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string') {
            message = (err as any).message;
          }
          if (message.toLowerCase().includes('failed to fetch')) {
            message = 'Upload timed out. Please retry, or try a smaller JPG/PNG image.';
          }
          onUploadError?.(message);
          onChange((prev) =>
            prev.map((img) =>
              img.id === newImages[i].id ? { ...img, uploading: false } : img
            )
          );
        }
      }
    },
    [images, onChange, userId, maxImages, onUploadError]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((img) => img.id === active.id);
      const newIndex = images.findIndex((img) => img.id === over.id);
      onChange(arrayMove(images, oldIndex, newIndex));
    }
  };

  const removeImage = (id: string) => {
    const img = images.find((i) => i.id === id);
    if (img) URL.revokeObjectURL(img.preview);
    onChange(images.filter((i) => i.id !== id));
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
          dragOver
            ? 'border-blue-500 bg-blue-900/20 scale-[1.01]'
            : 'border-slate-700 bg-slate-900 hover:border-slate-600'
        }`}
      >
        <Upload className="w-10 h-10 mx-auto text-slate-500 mb-3" />
        <p className="text-sm font-medium text-slate-300">
          Drop image here or click to browse
        </p>
        <p className="text-xs text-slate-500 mt-1">
          JPG, PNG, WebP. Max {maxImages} image.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {images.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={images.map((i) => i.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-1 gap-3">
              {images.map((image) => (
                <SortableImage
                  key={image.id}
                  image={image}
                  onRemove={() => removeImage(image.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
